import { Camera, Clock, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, fetchFile } from '../lib/api';
import { prepareSample, type SampleUpload } from '../lib/samples';
import type { Child, FieldTrip } from '../lib/types';
import { useParent } from './ParentContext';
import { canDo } from './Plans';

/**
 * Field trips (#622, part of #453), on the Portfolio tab: one trip for every child who went, with photos (each child's
 * work samples) and, if it counts as school time, hours added to that day's attendance.
 */

const fmt = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const month = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short' }).toUpperCase();
const today = () => new Date().toLocaleDateString('en-CA');
const SUBJECTS = ['Science', 'History', 'Social Studies', 'Art', 'Music', 'Geography', 'Physical Education'];

interface Draft {
  id?: string;
  date: string;
  place: string;
  learned: string;
  kids: string[];
  count: boolean;
  hours: number;
  subject: string;
  files: SampleUpload[];
}
interface Data {
  enabled: boolean;
  maxPhotos: number;
  maxHours: number;
  trips: FieldTrip[];
}

export function FieldTripsCard({ child }: { child: Child }) {
  const { toast, children } = useParent();
  const [data, setData] = useState<Data | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const canEdit = canDo(child, 'attendance', 'edit');
  const load = useCallback(() => {
    api
      .get<Data>(`/api/children/${child.id}/field-trips`)
      .then(setData)
      .catch(() => setData(null));
  }, [child.id]);
  useEffect(load, [load]);

  // Photos are fetched with the sign-in and shown from memory.
  useEffect(() => {
    let live = true;
    for (const p of (data?.trips ?? []).flatMap((t) => t.photos).filter((x) => x.mime.startsWith('image/') && !thumbs[x.id])) {
      fetchFile(`/api/samples/${p.id}`)
        .then((b) => live && setThumbs((cur) => ({ ...cur, [p.id]: URL.createObjectURL(b) })))
        .catch(() => undefined);
    }
    return () => {
      live = false;
    };
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data?.enabled) return null;
  const blank = (): Draft => ({ date: today(), place: '', learned: '', kids: [child.id], count: true, hours: Math.min(3, data.maxHours), subject: '', files: [] });
  const edit = (t: FieldTrip): Draft => ({
    id: t.id,
    date: t.date,
    place: t.place,
    learned: t.learned,
    kids: t.children.map((c) => c.id),
    count: t.hours > 0,
    hours: t.hours || Math.min(3, data.maxHours),
    subject: t.subject,
    files: [],
  });
  const set = (patch: Partial<Draft>) => draft && setDraft({ ...draft, ...patch });
  const counted = data.trips.reduce((a, t) => a + t.hours, 0);
  const photosLeft = draft ? data.maxPhotos - (draft.id ? (data.trips.find((t) => t.id === draft.id)?.photos.length ?? 0) : 0) - draft.files.length : 0;

  async function save() {
    if (!draft) return;
    setBusy(true);
    try {
      const body = {
        date: draft.date,
        place: draft.place,
        learned: draft.learned,
        children: draft.kids,
        hours: draft.count ? draft.hours : 0,
        subject: draft.count ? draft.subject : '',
        files: draft.files.map(({ name, data: d }) => ({ name, data: d })),
      };
      if (draft.id) await api.put(`/api/children/${child.id}/field-trips/${draft.id}`, body);
      else await api.post(`/api/children/${child.id}/field-trips`, body);
      const names = children.filter((c) => draft.kids.includes(c.id)).map((c) => c.name);
      setDraft(null);
      load();
      toast({ text: `Saved for ${names.join(' and ') || child.name}.${body.hours ? ` ${body.hours} hours added to ${fmt(body.date)}.` : ''}`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  async function remove(t: FieldTrip) {
    const who = t.children.map((c) => c.name).join(' and ');
    if (!confirm(`Remove the trip to ${t.place} for ${who}? ${t.hours ? 'Its hours come off their attendance, and its' : 'Its'} photos are removed too.`)) return;
    try {
      await api.del(`/api/children/${child.id}/field-trips/${t.id}`);
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <section className="card stack trips-card">
      <div className="row-between wrap">
        <h2>Field trips</h2>
        <div className="row-gap">
          {data.trips.length > 0 && (
            <span className="muted small">
              {data.trips.length} trip{data.trips.length === 1 ? '' : 's'} · {counted} h counted
            </span>
          )}
          {canEdit && (
            <button className="btn small" onClick={() => setDraft(draft ? null : blank())}>
              {draft ? 'Cancel' : (
                <>
                  <Plus size={15} /> Add a trip
                </>
              )}
            </button>
          )}
        </div>
      </div>
      <p className="muted small">Where you went and what {child.name} learned. Photos go in as work samples, and a trip can count as school time.</p>
      {draft && (
        <div className="test-form stack">
          <div className="field-row wrap-row">
            <label>
              Date
              <input type="date" value={draft.date} onChange={(e) => set({ date: e.target.value })} />
            </label>
            <label className="grow">
              Where
              <input value={draft.place} onChange={(e) => set({ place: e.target.value })} maxLength={120} placeholder="e.g. Franklin Institute" />
            </label>
          </div>
          <label>
            What they learned
            <textarea rows={2} value={draft.learned} onChange={(e) => set({ learned: e.target.value })} maxLength={2000} placeholder="e.g. Built a circuit in the electricity lab" />
          </label>
          {children.length > 1 && (
            <div className="row-gap wrap" role="group" aria-label="Who went">
              <span className="muted small">Who went</span>
              {children.map((c) => {
                const on = draft.kids.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`chip ${on ? 'active' : ''}`}
                    aria-pressed={on}
                    disabled={c.id === child.id}
                    onClick={() => set({ kids: on ? draft.kids.filter((k) => k !== c.id) : [...draft.kids, c.id] })}
                  >
                    {on ? '✓ ' : ''}
                    {c.name}
                  </button>
                );
              })}
            </div>
          )}
          <div className="trip-count">
            <label className="check">
              <input type="checkbox" checked={draft.count} onChange={(e) => set({ count: e.target.checked })} />
              <span>Count it as school time</span>
            </label>
            {draft.count && (
              <div className="row-gap wrap">
                <label className="inline-field">
                  <input type="number" min={0.5} max={data.maxHours} step={0.5} value={draft.hours} onChange={(e) => set({ hours: Number(e.target.value) })} aria-label="Hours" /> hours
                </label>
                <label className="inline-field">
                  in
                  <input list="trip-subjects" value={draft.subject} onChange={(e) => set({ subject: e.target.value })} maxLength={60} placeholder="Subject (optional)" aria-label="Subject" />
                </label>
                <datalist id="trip-subjects">
                  {SUBJECTS.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </div>
            )}
          </div>
          {draft.count && <p className="muted small">Added to each child’s recorded hours on {fmt(draft.date)}, and taken off again if you remove the trip.</p>}
          <div className="row-between wrap">
            <label className={`btn small file-pick ${photosLeft <= 0 ? 'disabled' : ''}`}>
              <Camera size={15} /> {draft.files.length ? `${draft.files.length} photo${draft.files.length === 1 ? '' : 's'} to add` : 'Add photos'}
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                hidden
                disabled={photosLeft <= 0}
                onChange={async (e) => {
                  const picked = [...(e.target.files ?? [])].slice(0, Math.max(0, photosLeft));
                  e.target.value = '';
                  try {
                    const ready: SampleUpload[] = [];
                    for (const f of picked) ready.push(await prepareSample(f, 25));
                    set({ files: [...draft.files, ...ready] });
                  } catch (err) {
                    toast({ text: (err as Error).message, tone: 'alert' });
                  }
                }}
              />
            </label>
            <button className="btn primary" disabled={busy || !draft.place.trim() || !draft.date || !draft.kids.length} onClick={save}>
              {busy ? 'Saving…' : 'Save trip'}
            </button>
          </div>
        </div>
      )}
      {!data.trips.length && !draft && <p className="muted">No field trips yet.</p>}
      {data.trips.map((t) => (
        <div key={t.id} className="trip-row">
          <div className="trip-date" aria-hidden>
            <span>{month(t.date)}</span>
            <strong>{Number(t.date.slice(8))}</strong>
          </div>
          <div className="stack tight grow">
            <div className="row-between wrap">
              <strong>{t.place}</strong>
              <span className="muted small">
                {fmt(t.date)}
                {t.children.length > 1 && ` · ${t.children.map((c) => c.name).join(' & ')}`}
              </span>
            </div>
            {t.learned && <span className="small">{t.learned}</span>}
            <div className="score-chips">
              <span className={`split-chip ${t.hours ? 'good' : ''}`}>
                <Clock size={12} aria-hidden /> {t.hours ? `${t.hours} h counted${t.subject ? ` · ${t.subject}` : ''}` : 'Not counted as school time'}
              </span>
              {t.photos.length > 0 && (
                <span className="split-chip">
                  <Camera size={12} aria-hidden /> {t.photos.length} photo{t.photos.length === 1 ? '' : 's'} in work samples
                </span>
              )}
            </div>
            {t.photos.some((p) => thumbs[p.id]) && (
              <div className="trip-photos">
                {t.photos.map((p) => thumbs[p.id] && <img key={p.id} src={thumbs[p.id]} alt={p.name} />)}
              </div>
            )}
          </div>
          {canEdit && (
            <div className="row-gap">
              <button className="btn small" onClick={() => setDraft(edit(t))}>
                Edit
              </button>
              <button className="icon-btn" aria-label={`Remove the trip to ${t.place}`} onClick={() => remove(t)}>
                <Trash2 size={15} />
              </button>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
